// client/src/features/salesCoach/components/TestCoach.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import { ArrowLeft, Send, ShieldCheck, HelpCircle } from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";

const TestCoach = ({ id }) => {
  const [training, setTraining] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const messageEndRef = useRef(null);

  useEffect(() => {
    loadTestEnvironment();
  }, [id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadTestEnvironment = async () => {
    setLoading(true);
    try {
      const t = await salesCoachApi.getTraining(id);
      setTraining(t);
      setRules(t.rules || []);

      // Create a simulated rep learning session to test the coach
      const s = await salesCoachApi.createUserSession({
        trainingId: id,
        mode: "learn",
      });
      setSession(s.data);
      setMessages(s.messages || []);
    } catch (err) {
      console.error("Failed to load test environment:", err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const messageText = input.trim();
    setInput("");
    setSending(true);

    const tempUserMsg = {
      id: Date.now().toString(),
      role: "user",
      content: messageText,
    };
    setMessages((prev) => [...prev, tempUserMsg]);

    try {
      const res = await salesCoachApi.sendUserMessage(
        session.id,
        messageText,
        false,
      );
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempUserMsg.id),
        res.userMessage,
        res.modelMessage,
      ]);
    } catch (err) {
      console.error("Failed to get response:", err);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader text="Initializing test sandbox..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] bg-gray-50">
      {/* Header */}
      <div className="px-6 py-4 bg-white border-b border-gray-200 flex justify-between items-center flex-shrink-0 shadow-sm">
        <div className="flex items-center gap-3">
          <Link
            to="/oa/sales-coach"
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-teal-600 bg-teal-50 px-2 py-0.5 rounded">
              Test Sandbox
            </span>
            <h1 className="text-lg font-bold text-gray-800 mt-0.5">
              Testing: {training?.product_name}
            </h1>
          </div>
        </div>
      </div>

      {/* Split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left rules panel */}
        <div className="hidden lg:flex w-80 bg-white border-r border-gray-200 p-6 flex-col gap-6 overflow-y-auto">
          <div>
            <h3 className="text-sm font-bold text-gray-800 flex items-center">
              <ShieldCheck className="w-4 h-4 mr-2 text-secondary" />
              Active Guidelines
            </h3>
            <p className="mt-1 text-xs text-gray-400">
              The AI Coach uses these memory rules to evaluate and guide user
              questions.
            </p>
            <div className="mt-4 space-y-2">
              {rules.length === 0 ? (
                <p className="text-xs text-gray-400 italic">
                  No rules active yet.
                </p>
              ) : (
                rules
                  .filter((r) => r.is_enabled)
                  .map((r) => (
                    <div
                      key={r.id}
                      className="p-2.5 bg-secondary-ultra-light/30 border border-[#207C9720] rounded-xl text-xs text-gray-800 font-medium"
                    >
                      {r.rule_text}
                    </div>
                  ))
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-bold text-gray-800 flex items-center">
              <HelpCircle className="w-4 h-4 mr-2 text-gray-500" />
              Test Queries
            </h3>
            <div className="mt-3 space-y-2 text-xs text-gray-500">
              <p
                className="p-2 bg-gray-50 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() =>
                  setInput("What are the key benefits of this product?")
                }
              >
                "What are the key benefits?"
              </p>
              <p
                className="p-2 bg-gray-50 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() =>
                  setInput("How should I sell this to an enterprise client?")
                }
              >
                "How to pitch enterprise?"
              </p>
              <p
                className="p-2 bg-gray-50 border border-gray-100 rounded-lg cursor-pointer hover:bg-gray-100"
                onClick={() =>
                  setInput(
                    "What should I say if they think the price is too high?",
                  )
                }
              >
                "How to handle price objections?"
              </p>
            </div>
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col justify-between overflow-hidden relative">
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
            {messages.map((m) => (
              <div
                key={m.id || Math.random().toString()}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 rounded-2xl text-sm shadow-sm ${
                    m.role === "user"
                      ? "bg-secondary text-white rounded-tr-none"
                      : "bg-white text-gray-800 border border-gray-200 rounded-tl-none"
                  }`}
                >
                  <div className="whitespace-pre-wrap leading-relaxed">
                    {m.content}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messageEndRef} />
          </div>

          {/* Form */}
          <div className="p-4 bg-white border-t border-gray-200 flex-shrink-0 shadow-lg">
            <form
              onSubmit={handleSend}
              className="flex gap-2 max-w-4xl mx-auto items-center"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the coach a question to test its knowledge..."
                disabled={sending}
                className="flex-1 block rounded-xl border border-gray-300 px-4 py-3 text-gray-900 focus:border-secondary focus:outline-none focus:ring-1 focus:ring-secondary text-sm"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="p-3 bg-secondary hover:bg-secondary-light text-white rounded-xl disabled:opacity-50 transition-all flex items-center justify-center flex-shrink-0 shadow-md"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestCoach;
